/**
 * Every user-facing string, in both languages.
 *
 * `fr` is typed as `typeof en`, so a translation missing anywhere — a whole
 * key, or one argument of a function — is a compile error, not a silent
 * fallback to English discovered by a French speaker in production.
 */

import type { BedOption, BedType } from '@room-mates/shared';

function enCount(n: number, singular: string, plural = `${singular}s`): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

/** French counts 0 and 1 as singular (CLDR "one" category), 2+ as plural. */
function frCount(n: number, singular: string, plural: string): string {
  return `${n} ${n <= 1 ? singular : plural}`;
}

export const en = {
  /** Labels for the fixed catalogues in `@room-mates/shared` — keyed by their stable value, not translated there. */
  catalog: {
    bedType: { single: 'Single', double: 'Double' },
    gender: { woman: 'Woman', man: 'Man', other: 'Other' },
    bedOption: {
      standard: 'Standard',
      bunk_bottom: 'Bunk (bottom)',
      bunk_top: 'Bunk (top)',
      sofa_bed: 'Sofa bed',
      couch: 'Couch',
      inflatable: 'Inflatable',
    },
    /** Parenthesis-free, for composing "Type Kind" — "Bunk Bottom", not "Bunk (bottom)". */
    bedKind: {
      standard: 'Standard',
      bunk_bottom: 'Bunk Bottom',
      bunk_top: 'Bunk Top',
      sofa_bed: 'Sofa Bed',
      couch: 'Couch',
      inflatable: 'Inflatable',
    },
    guestOption: {
      snoring: {
        label: 'Snores',
        description: 'Keeps light sleepers awake; best grouped with other snorers.',
      },
      handicap: {
        label: 'Reduced mobility',
        description: 'Needs a bed that can be reached without climbing.',
      },
      allergies: {
        label: 'Allergies',
        description: 'Recorded for the organiser; no automatic placement rule yet.',
      },
    },
  },

  landing: {
    heroTitle: 'Work out who sleeps where.',
    heroPitch:
      'Describe your houses down to the bed, add your guests and what matters to them, and let the planner work out a night-by-night sleeping arrangement — with the reasoning behind every placement.',
    getStarted: 'Create an account',
    featuresTitle: 'What it does',
    feature1Title: 'Houses, rooms & beds',
    feature1Description:
      'Describe your houses down to the bed: type, size, and the kind of sleeping place it is. Bed names are generated, never typed, so they are always correct.',
    feature2Title: 'Guests, exactly as they are',
    feature2Description:
      'Gender, traits, who they would rather room with, and who — or where — they must be kept away from.',
    feature3Title: 'Sojourns',
    feature3Description:
      'A named stay. Houses join it with the nights they are usable; guests join it with the nights they are actually there.',
    feature4Title: 'A plan you can explain',
    feature4Description:
      'One click builds a night-by-night sleeping plan, with the reasoning behind every placement and what else would have worked just as well. Lock anything by hand and regenerate freely.',
    stepsTitle: 'How it works',
    step1Title: 'Add your houses, rooms and beds',
    step1Description: 'Down to the mattress: type, size, and how many people it sleeps.',
    step2Title: 'Add your guests and their preferences',
    step2Description: 'Gender, traits, who they want to room with, who they must avoid.',
    step3Title: 'Create a sojourn',
    step3Description: 'A named stay. Add the houses and guests taking part, and their dates.',
    step4Title: 'Generate a plan',
    step4Description: 'One click places every guest, with the reasoning behind each placement.',
    step5Title: 'Review, lock, and adjust',
    step5Description: 'Pin a placement by hand if you need to, and regenerate the rest freely.',
    ctaTitle: 'Ready to plan your next stay?',
  },

  nav: {
    brand: 'Room-mates',
    sojourns: 'Sojourns',
    calendar: 'Calendar',
    houses: 'Houses',
    rooms: 'Rooms',
    guests: 'Guests',
    admin: 'Admin',
    signOut: 'Sign out',
    mainNav: 'Main',
    language: 'Language',
    somebody: 'somebody',
    languageToggle: (target: 'en' | 'fr'): string =>
      target === 'fr' ? 'Switch to French' : 'Switch to English',
    themeToggle: (target: 'light' | 'dark'): string =>
      target === 'dark' ? 'Switch to dark theme' : 'Switch to light theme',
    viewingAs: (viewing: string, administrator: string) => (
      <>
        Viewing as <strong>{viewing}</strong>. Anything you change is changed for them. Signed in as{' '}
        {administrator}.
      </>
    ),
    stopViewingAs: (viewing: string) => `Stop viewing as ${viewing}`,
  },

  common: {
    cancel: 'Cancel',
    saveChanges: 'Save changes',
    delete: 'Delete',
    remove: 'Remove',
    edit: 'Edit',
    done: 'Done',
    sure: 'Sure?',
    noAddressRecorded: 'No address recorded',
    clearFilter: 'Clear the filter',
    todayButton: 'Today',
    close: 'Close',
    loading: 'Loading…',
    restoringSession: 'Restoring your session…',
  },

  count: {
    guests: (n: number) => enCount(n, 'guest'),
    houses: (n: number) => enCount(n, 'house', 'houses'),
    rooms: (n: number) => enCount(n, 'room'),
    beds: (n: number) => enCount(n, 'bed'),
    nights: (n: number) => enCount(n, 'night'),
    people: (n: number) => enCount(n, 'person', 'people'),
    accounts: (n: number) => enCount(n, 'account'),
    sojourns: (n: number) => enCount(n, 'sojourn'),
    places: (n: number) => enCount(n, 'place'),
    doubleBookedBeds: (n: number) => enCount(n, 'double-booked bed'),
  },

  auth: {
    tagline: 'Plan who sleeps where, and why.',
    usernameLabel: 'Username',
    passwordLabel: 'Password',
    signInTitle: 'Sign in',
    signInSubmit: 'Sign in',
    noAccountYet: 'No account yet? ',
    createOne: 'Create one',
    registerTitle: 'Create an account',
    registerSubmit: 'Create account',
    passwordHint: 'At least 8 characters.',
    alreadyRegistered: 'Already registered? ',
    signInLink: 'Sign in',
  },

  sojournsPage: {
    title: 'Sojourns',
    subtitle: 'A sojourn is a period with houses and guests in it. Plans are made per sojourn.',
    newSojourn: 'New sojourn',
    filterLabel: 'Filter sojourns',
    filterPlaceholder: 'Filter by name or date…',
    emptyTitle: 'No sojourns yet',
    emptyDescription: 'Create one, add your houses and guests, then ask for a plan.',
    noMatchesTitle: 'No sojourn matches',
    noMatchesDescription: (query: string) => `Nothing here matches "${query}".`,
    planned: 'Planned',
    viewThePlan: 'View the plan',
    makeAPlan: 'Make a plan',
    createDialogTitle: 'New sojourn',
    nameLabel: 'Name',
    nameHint: 'Unique among your sojourns. Numbers help: Virgo 01, Virgo 02…',
    create: 'Create',
  },

  sojournDetail: {
    backToSojourns: 'Back to sojourns',
    editDates: 'Edit dates',
    dayByDayRecap: 'Day by day recap',
  },

  editSojournDates: {
    title: 'Edit dates',
    saveDates: 'Save dates',
  },

  sojournHouses: {
    title: 'Houses',
    addHouse: 'Add a house to this sojourn',
    addHousePlaceholder: 'Add a house…',
    emptyDescription:
      'No house yet. Add one, and it will be available for the whole sojourn unless you say otherwise.',
    availableRanges: (ranges: string) => `Available ${ranges}`,
    availability: 'Availability',
  },

  sojournGuests: {
    title: 'Guests',
    filterByTag: 'Filter guests to add by tag',
    anyTag: 'Any tag',
    addGuest: 'Add a guest to this sojourn',
    addGuestPlaceholder: 'Add a guest…',
    addEveryone: (tag: string | null) => `Add everyone${tag === null ? '' : ` tagged "${tag}"`}`,
    emptyDescription:
      'Nobody yet. Guests you add stay for the whole sojourn unless you give them their own dates.',
    dates: 'Dates',
  },

  sojournItinerary: {
    title: 'Itinerary',
    subtitle: (hasPlan: boolean) =>
      `Arrivals, departures${hasPlan ? ' and bed changes' : ''}, day by day.`,
    exportCsv: 'Export CSV',
    loadingPlan: 'Loading the plan…',
    emptyTitle: 'Nothing scheduled yet',
    emptyDescription: 'Add guests with dates to see their arrivals and departures here.',
    dateHeader: 'Date',
    eventsHeader: 'Events',
    notesHeader: 'Notes',
    notePlaceholder: 'Hour, transport…',
    noteFor: (date: string) => `Note for ${date}`,
    arrives: (name: string) => `${name} arrives`,
    departs: (name: string) => `${name} departs`,
    movesTo: (name: string, from: string, to: string) => `${name} moves from ${from} to ${to}`,
    csvHeaders: ['Date', 'Events', 'Notes'] as readonly [string, string, string],
  },

  planLocks: {
    title: 'Locked placements',
    subtitle: 'Pin a guest to a room the planner must respect, with your reason.',
    addLock: 'Add a lock',
    loadingLocks: 'Loading locks…',
    emptyDescription: 'No locks. The planner is free to place everybody.',
    wholeStay: 'Whole stay',
    dialogTitle: 'Lock a guest to a room',
    guestLabel: 'Guest',
    chooseGuest: 'Choose a guest…',
    roomLabel: 'Room',
    chooseRoom: 'Choose a room…',
    onlyPartOfStay: 'Only for part of the stay',
    whyLabel: 'Why',
    whyHint: 'Recorded on the plan so the decision can be explained later.',
    lockPlacement: 'Lock placement',
  },

  dateRangeCalendar: {
    previousYear: 'Previous year',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    nextYear: 'Next year',
    pickArrival: 'Pick the arrival day.',
    pickDeparture: (start: string) => `${start} → pick the departure day.`,
    summary: (start: string, end: string, nights: string) => `${start} → ${end} · ${nights}`,
  },

  dateRangeEditor: {
    addPeriod: 'Add period',
    addAPeriod: 'Add a period',
  },

  dayByDayRecap: {
    titleFor: (sojournName: string) => `Day by day — ${sojournName}`,
    loadingRecap: 'Loading the recap…',
    previousNight: '‹ Previous night',
    nextNight: 'Next night ›',
    play: '▶ Play',
    pause: '⏸ Pause',
    nightOf: (index: number, total: number, date: string) => `Night ${index} of ${total} · ${date}`,
    noHouses: 'No houses are part of this sojourn yet.',
    noPlan: 'Generate a plan to see who sleeps where each night.',
    free: 'Free',
    withoutABedTonight: 'Without a bed tonight',
  },

  calendarPage: {
    title: 'Calendar',
    subtitle: 'Pick a date or a range to see who is in every bed then.',
    showingSingle: (date: string) => `Showing ${date}. Click another day to see a range instead.`,
    showingRange: (start: string, end: string, nights: string) =>
      `Showing ${start} → ${end} · ${nights}.`,
    loadingOccupancy: 'Loading occupancy…',
    noBedsTitle: 'No beds yet',
    noBedsDescription: 'Add houses, rooms and beds to see their occupancy here.',
    bedsOccupiedOf: (occupied: string, total: number) => `${occupied} occupied of ${total}`,
    withoutABedTitle: 'Without a bed',
    withoutABedDescription: 'The planner could not seat everyone during this window.',
    free: 'Free',
  },

  guestsPage: {
    title: 'Guests',
    subtitle: 'The people to house, with what they need and who they get on with.',
    addGuest: 'Add a guest',
    filterLabel: 'Filter guests',
    filterPlaceholder: 'Filter by name, trait or note…',
    emptyTitle: 'No guests yet',
    emptyDescription: 'Add the people coming, then record their preferences.',
    noMatchesTitle: 'No guest matches',
    noMatchesForQuery: (query: string) => `Nobody here matches "${query}".`,
    noMatchesForTags: 'Nobody has all of the selected tags.',
    clearFilters: 'Clear the filters',
    wantsToRoomWith: (names: string) => `Wants to room with ${names}`,
    neverWith: (names: string) => `Never with ${names}`,
    refuses: (names: string) => `Refuses ${names}`,
    someone: 'someone',
    aRoom: 'a room',
  },

  guestDialog: {
    addTitle: 'Add a guest',
    editTitle: (name: string) => `Edit ${name}`,
    nameLabel: 'Name',
    genderLabel: 'Gender',
    genderHint: 'Helps the planner keep genders together where it can.',
    regenerateAvatar: 'Redraw avatar',
    traitsLabel: 'Traits',
    traitsHint: 'These change where the planner is willing to put them.',
    tagsLabel: 'Tags',
    tagsHint: 'Free-form labels for grouping and filtering guests.',
    addATag: 'Add a tag',
    addTagPlaceholder: 'Add a tag and press Enter…',
    prefersToRoomWith: 'Prefers to room with',
    prefersToRoomWithHint: 'The planner tries to put them in the same room.',
    mustNotRoomWith: 'Must not room with',
    mustNotRoomWithHint: 'A hard rule: never the same room.',
    mustNotSleepIn: 'Must not sleep in',
    mustNotSleepInHint: 'Rooms this guest cannot be placed in.',
    notesLabel: 'Notes',
    notesHint: 'Optional, for your own reference.',
    addGuest: 'Add guest',
    saveGuest: 'Save guest',
    remove: 'Remove',
    add: 'Add…',
    nothingLeftToChoose: 'Nothing left to choose.',
    nobodyYet: 'Nobody yet.',
    discardTitle: 'Discard changes?',
    discardMessage: 'You have unsaved changes. Save or discard them before leaving.',
    keepEditing: 'Keep editing',
    discardChanges: 'Discard changes',
  },

  housesPage: {
    title: 'Houses',
    subtitle:
      'The places you can lodge people in. Each house holds rooms, and each room holds beds.',
    addHouse: 'Add a house',
    filterLabel: 'Filter houses',
    filterPlaceholder: 'Filter by name or address…',
    emptyTitle: 'No houses yet',
    emptyDescription: 'Add the first house to start building your lodging catalogue.',
    noMatchesTitle: 'No house matches',
    noMatchesForQuery: (query: string) => `Nothing here is called "${query}".`,
    dialogTitle: 'Add a house',
    nameHint: 'Must be unique among your houses.',
    addressLabel: 'Address',
    addressHint: 'Optional.',
    addHouseSubmit: 'Add house',
  },

  houseDetail: {
    backToHouses: 'Back to houses',
    addRoom: 'Add a room',
    detailsTitle: 'House details',
    nameLabel: 'Name',
    addressLabel: 'Address',
    noRoomsTitle: 'No rooms yet',
    noRoomsDescription: 'Add a room, then put beds in it.',
  },

  roomCard: {
    holdsUpTo: (people: string) => `Holds up to ${people}`,
    placesCapacity: (places: number, capacity: number) => `${places} places, capacity ${capacity}`,
    editRoom: 'Edit room',
    loadingBeds: 'Loading beds…',
    noBedsYet: 'No beds in this room yet.',
    addABed: 'Add a bed',
    custom: ' (custom)',
    sleeps: (n: number) => `sleeps ${n}`,
    wants: (names: string) => `wants ${names}`,
  },

  roomDialog: {
    addTitle: 'Add a room',
    editTitle: (name: string) => `Edit ${name}`,
    houseLabel: 'House',
    houseHint: 'A room belongs to one house, and keeps it for life.',
    chooseHouse: 'Choose a house…',
    nameLabel: 'Name',
    nameHint: 'Unique within its house.',
    maxPeopleLabel: 'Maximum people',
    maxPeopleHint:
      'The planner never puts more people than this in the room, however many beds it has.',
    addRoom: 'Add room',
    saveRoom: 'Save room',
  },

  bedDialog: {
    addTitle: 'Add a bed',
    editTitle: (name: string) => `Edit ${name}`,
    typeLabel: 'Type',
    typeOption: (label: string, sizeCm: number, sleeps: number) =>
      `${label} (${sizeCm} cm, sleeps ${sleeps})`,
    optionLabel: 'Kind of sleeping place',
    widthLabel: 'Width (cm)',
    widthHintDefault: (type: string, size: number) =>
      `Following the ${type} default: ${size} cm. Type a number to override it.`,
    widthHintCustom: 'Set by hand. Clear the box to follow the bed type again.',
    favoriteGuestsLabel: 'Favourite guests',
    favoriteGuestsHint: 'In order of preference. The planner tries hardest to satisfy the first.',
    moveUp: 'Move up',
    moveDown: 'Move down',
    remove: 'Remove',
    unknown: 'Unknown',
    addAFavoriteGuest: 'Add a favourite guest',
    addGuestPlaceholder: 'Add a guest…',
    addBed: 'Add bed',
    saveBed: 'Save bed',
  },

  roomsPage: {
    title: 'Rooms',
    subtitle: 'Every room you have, whichever house it belongs to.',
    addRoom: 'Add a room',
    filterLabel: 'Filter rooms',
    filterPlaceholder: 'Filter by room or house…',
    noRoomsTitle: 'No rooms yet',
    noRoomsDescription: 'Add a room and choose which house it belongs to.',
    noHousesTitle: 'No houses yet',
    noHousesDescription: 'A room lives in a house, so add a house first.',
    goToHouses: 'Go to houses',
    noMatchesTitle: 'No room matches',
    noMatchesForQuery: (query: string) => `Nothing here is called "${query}".`,
    holdsUpTo: (people: string) => `Holds up to ${people}`,
    manageItsBeds: 'Manage its beds',
  },

  planPage: {
    backToSojourns: 'Back to sojourns',
    titleFor: (sojournName: string) => `Plan — ${sojournName}`,
    noPlanYetFor: (start: string, end: string) => (
      <>
        No plan yet for {start} → {end}
      </>
    ),
    computedAt: (when: string) => `Computed ${when}`,
    editTheSojourn: 'Edit the sojourn',
    generateThePlan: 'Generate the plan',
    regenerate: 'Regenerate',
    shareDoubleBedsLabel:
      'Let two guests share a double bed — only ever guests who asked to room together',
    noPlanTitle: 'No plan yet',
    noPlanDescription: 'Generate one to see who sleeps where, and why.',
    nothingToArrangeTitle: 'Nothing to arrange',
    nothingToArrangeDescription: 'No guest is expected on any night.',
    periods: 'Periods',
    periodsHint:
      'The sojourn is cut wherever somebody arrives or leaves, or a house opens or closes.',
    ofGuestNightsHoused: 'of guest-nights housed',
    periodsLabel: 'periods',
    bedOccupancy: 'bed occupancy',
    midStayMoves: 'mid-stay moves',
    preferenceScore: 'preference score',
    toCompute: 'to compute',
    inThisRoom: (n: number) => `${n} in this room`,
    lockedByYou: 'Locked by you',
    hideDetails: 'Hide details',
    why: 'Why?',
    onSite: (n: number) => `${n} on site`,
    nobodyCouldBeFoundABedFor: 'Nobody could be found a bed for:',
  },

  planReasons: {
    equallyGoodAlternatives:
      'Equally good alternatives — apply any of these by hand without losing anything',
  },

  admin: {
    title: 'Administration',
    subtitle:
      'Accounts, the households they share, and a way to see the application through their eyes.',
    accountsTitle: 'Accounts',
    filterAccounts: 'Filter accounts',
    filterAccountsPlaceholder: 'Filter by username, household or role…',
    noAccountMatches: 'No account matches',
    noMatchesFor: (query: string) => `Nothing matches "${query}".`,
    administrator: 'Administrator',
    you: 'You',
    sharedByAccounts: (accounts: string) => `shared by ${accounts}`,
    householdToThemselves: 'household to themselves',
    viewAs: 'View as',
    shareData: 'Share data',
    setPassword: 'Set password',
    leaveHousehold: 'Leave household',
    leaveTakingNothing: 'Leave, taking nothing?',
    householdsTitle: 'Households',
    householdsSubtitle: 'A household is the pool of houses, guests and sojourns its members share.',
    filterHouseholds: 'Filter households',
    filterHouseholdsPlaceholder: 'Filter by household or member…',
    noHouseholdMatches: 'No household matches',
    noMembers: 'No members',
    rename: 'Rename',
    renameOf: (name: string) => `Rename ${name}`,
    save: 'Save',
  },

  setPasswordDialog: {
    titleFor: (username: string) => `Set a password for ${username}`,
    warning: (username: string) => (
      <>
        Everyone signed in as {username} is signed out immediately. Tell them the new password
        yourself — it is never shown again.
      </>
    ),
    newPasswordLabel: 'New password',
    passwordHint: 'At least 8 characters.',
    setPassword: 'Set password',
  },

  shareHouseholdDialog: {
    sharedTitle: 'Shared',
    sharedMessage: (username: string, householdName: string) => (
      <>
        {username} now shares the household &ldquo;{householdName}&rdquo;.
      </>
    ),
    renamedToAvoidClash: 'Renamed to avoid a clash',
    nothingRenamed: 'Nothing had to be renamed.',
    doneButton: 'Done',
    titleFor: (username: string) => `Share ${username}'s data`,
    warning: (username: string) => (
      <>
        {username}&apos;s household is poured into the one you choose, and everyone in both sees all
        of it. This cannot be undone — leaving a shared household later takes nothing back out of
        it.
      </>
    ),
    shareWithLabel: 'Share with',
    shareWithHint: 'Their household absorbs this one.',
    chooseAnAccount: 'Choose an account…',
    share: 'Share',
  },

  filterInput: {
    matchOf: (matchCount: number, totalCount: number) => `${matchCount} of ${totalCount} shown`,
    clear: 'Clear',
  },

  doorTransition: {
    greeting: 'Hi!',
  },
};

export const fr: typeof en = {
  catalog: {
    bedType: { single: 'Simple', double: 'Double' },
    gender: { woman: 'Femme', man: 'Homme', other: 'Autre' },
    bedOption: {
      standard: 'Standard',
      bunk_bottom: 'Superposé (bas)',
      bunk_top: 'Superposé (haut)',
      sofa_bed: 'Canapé-lit',
      couch: 'Canapé',
      inflatable: 'Matelas gonflable',
    },
    bedKind: {
      standard: 'Standard',
      bunk_bottom: 'Superposé Bas',
      bunk_top: 'Superposé Haut',
      sofa_bed: 'Canapé-lit',
      couch: 'Canapé',
      inflatable: 'Matelas Gonflable',
    },
    guestOption: {
      snoring: {
        label: 'Ronfle',
        description:
          'Empêche les dormeurs légers de dormir ; à regrouper de préférence avec d’autres ronfleurs.',
      },
      handicap: {
        label: 'Mobilité réduite',
        description: 'A besoin d’un lit accessible sans avoir à grimper.',
      },
      allergies: {
        label: 'Allergies',
        description:
          "Enregistré pour l'organisateur ; aucune règle de placement automatique pour l'instant.",
      },
    },
  },

  landing: {
    heroTitle: 'Sachez qui dort où.',
    heroPitch:
      'Décrivez vos maisons jusqu’au lit, ajoutez vos invités et ce qui compte pour eux, et laissez le plan établir une répartition nuit par nuit — avec le raisonnement derrière chaque placement.',
    getStarted: 'Créer un compte',
    featuresTitle: 'Ce que ça fait',
    feature1Title: 'Maisons, chambres et lits',
    feature1Description:
      'Décrivez vos maisons jusqu’au lit : type, taille et sorte de couchage. Les noms des lits sont générés, jamais saisis, donc toujours corrects.',
    feature2Title: 'Des invités, tels qu’ils sont',
    feature2Description:
      'Genre, traits, avec qui ils préfèrent partager une chambre, et avec qui — ou où — ils doivent être tenus à l’écart.',
    feature3Title: 'Séjours',
    feature3Description:
      'Une période nommée. Les maisons y participent avec leurs nuits disponibles ; les invités avec leurs nuits sur place.',
    feature4Title: 'Un plan que vous pouvez expliquer',
    feature4Description:
      'Un clic construit un plan de couchage nuit par nuit, avec le raisonnement derrière chaque placement et ce qui aurait aussi bien fonctionné. Verrouillez à la main ce que vous voulez, régénérez le reste librement.',
    stepsTitle: 'Comment ça marche',
    step1Title: 'Ajoutez vos maisons, chambres et lits',
    step1Description: 'Jusqu’au matelas : type, taille, et combien de personnes il accueille.',
    step2Title: 'Ajoutez vos invités et leurs préférences',
    step2Description: 'Genre, traits, avec qui ils veulent partager une chambre, qui éviter.',
    step3Title: 'Créez un séjour',
    step3Description:
      'Une période nommée. Ajoutez les maisons et invités concernés, et leurs dates.',
    step4Title: 'Générez un plan',
    step4Description:
      'Un clic place chaque invité, avec le raisonnement derrière chaque placement.',
    step5Title: 'Vérifiez, verrouillez, ajustez',
    step5Description: 'Épinglez un placement à la main si besoin, et régénérez le reste librement.',
    ctaTitle: 'Prêt à planifier votre prochain séjour ?',
  },

  nav: {
    brand: 'Room-mates',
    sojourns: 'Séjours',
    calendar: 'Calendrier',
    houses: 'Maisons',
    rooms: 'Chambres',
    guests: 'Invités',
    admin: 'Administration',
    signOut: 'Se déconnecter',
    mainNav: 'Principal',
    language: 'Langue',
    somebody: "quelqu'un",
    languageToggle: (target) => (target === 'fr' ? 'Passer en français' : 'Passer en anglais'),
    themeToggle: (target) =>
      target === 'dark' ? 'Passer au thème sombre' : 'Passer au thème clair',
    viewingAs: (viewing, administrator) => (
      <>
        Vous voyez l&apos;application comme <strong>{viewing}</strong>. Vos actions
        s&apos;appliquent à cette personne. Connecté en tant que {administrator}.
      </>
    ),
    stopViewingAs: (viewing) => `Arrêter de voir comme ${viewing}`,
  },

  common: {
    cancel: 'Annuler',
    saveChanges: 'Enregistrer les modifications',
    delete: 'Supprimer',
    remove: 'Retirer',
    edit: 'Modifier',
    done: 'Terminé',
    sure: 'Confirmer ?',
    noAddressRecorded: 'Aucune adresse renseignée',
    clearFilter: 'Effacer le filtre',
    todayButton: "Aujourd'hui",
    close: 'Fermer',
    loading: 'Chargement…',
    restoringSession: 'Restauration de votre session…',
  },

  count: {
    guests: (n) => frCount(n, 'invité', 'invités'),
    houses: (n) => frCount(n, 'maison', 'maisons'),
    rooms: (n) => frCount(n, 'chambre', 'chambres'),
    beds: (n) => frCount(n, 'lit', 'lits'),
    nights: (n) => frCount(n, 'nuit', 'nuits'),
    people: (n) => frCount(n, 'personne', 'personnes'),
    accounts: (n) => frCount(n, 'compte', 'comptes'),
    sojourns: (n) => frCount(n, 'séjour', 'séjours'),
    places: (n) => frCount(n, 'place', 'places'),
    doubleBookedBeds: (n) => frCount(n, 'lit en double réservation', 'lits en double réservation'),
  },

  auth: {
    tagline: 'Organisez qui dort où, et pourquoi.',
    usernameLabel: "Nom d'utilisateur",
    passwordLabel: 'Mot de passe',
    signInTitle: 'Se connecter',
    signInSubmit: 'Se connecter',
    noAccountYet: 'Pas encore de compte ? ',
    createOne: 'Créez-en un',
    registerTitle: 'Créer un compte',
    registerSubmit: 'Créer le compte',
    passwordHint: 'Au moins 8 caractères.',
    alreadyRegistered: 'Déjà inscrit ? ',
    signInLink: 'Se connecter',
  },

  sojournsPage: {
    title: 'Séjours',
    subtitle:
      'Un séjour est une période avec des maisons et des invités. Les plans sont établis par séjour.',
    newSojourn: 'Nouveau séjour',
    filterLabel: 'Filtrer les séjours',
    filterPlaceholder: 'Filtrer par nom ou par date…',
    emptyTitle: 'Aucun séjour pour le moment',
    emptyDescription: 'Créez-en un, ajoutez vos maisons et vos invités, puis demandez un plan.',
    noMatchesTitle: 'Aucun séjour ne correspond',
    noMatchesDescription: (query) => `Rien ici ne correspond à « ${query} ».`,
    planned: 'Planifié',
    viewThePlan: 'Voir le plan',
    makeAPlan: 'Créer un plan',
    createDialogTitle: 'Nouveau séjour',
    nameLabel: 'Nom',
    nameHint: 'Unique parmi vos séjours. Les numéros aident : Vierge 01, Vierge 02…',
    create: 'Créer',
  },

  sojournDetail: {
    backToSojourns: 'Retour aux séjours',
    editDates: 'Modifier les dates',
    dayByDayRecap: 'Récapitulatif jour par jour',
  },

  editSojournDates: {
    title: 'Modifier les dates',
    saveDates: 'Enregistrer les dates',
  },

  sojournHouses: {
    title: 'Maisons',
    addHouse: 'Ajouter une maison à ce séjour',
    addHousePlaceholder: 'Ajouter une maison…',
    emptyDescription:
      'Aucune maison pour le moment. Ajoutez-en une : elle sera disponible pour tout le séjour, sauf indication contraire.',
    availableRanges: (ranges) => `Disponible ${ranges}`,
    availability: 'Disponibilité',
  },

  sojournGuests: {
    title: 'Invités',
    filterByTag: 'Filtrer les invités à ajouter par étiquette',
    anyTag: 'Toutes les étiquettes',
    addGuest: 'Ajouter un invité à ce séjour',
    addGuestPlaceholder: 'Ajouter un invité…',
    addEveryone: (tag) =>
      `Ajouter tout le monde${tag === null ? '' : ` avec l'étiquette « ${tag} »`}`,
    emptyDescription:
      'Personne pour le moment. Les invités ajoutés restent tout le séjour, sauf si vous leur donnez leurs propres dates.',
    dates: 'Dates',
  },

  sojournItinerary: {
    title: 'Itinéraire',
    subtitle: (hasPlan) =>
      `Arrivées, départs${hasPlan ? ' et changements de lit' : ''}, jour par jour.`,
    exportCsv: 'Exporter en CSV',
    loadingPlan: 'Chargement du plan…',
    emptyTitle: 'Rien de prévu pour le moment',
    emptyDescription: 'Ajoutez des invités avec des dates pour voir leurs arrivées et départs ici.',
    dateHeader: 'Date',
    eventsHeader: 'Événements',
    notesHeader: 'Notes',
    notePlaceholder: 'Heure, transport…',
    noteFor: (date) => `Note pour le ${date}`,
    arrives: (name) => `${name} arrive`,
    departs: (name) => `${name} part`,
    movesTo: (name, from, to) => `${name} passe de ${from} à ${to}`,
    csvHeaders: ['Date', 'Événements', 'Notes'],
  },

  planLocks: {
    title: 'Placements verrouillés',
    subtitle: 'Épinglez un invité à une chambre que le plan doit respecter, avec votre raison.',
    addLock: 'Ajouter un verrou',
    loadingLocks: 'Chargement des verrous…',
    emptyDescription: 'Aucun verrou. Le plan est libre de placer tout le monde.',
    wholeStay: 'Tout le séjour',
    dialogTitle: 'Verrouiller un invité à une chambre',
    guestLabel: 'Invité',
    chooseGuest: 'Choisir un invité…',
    roomLabel: 'Chambre',
    chooseRoom: 'Choisir une chambre…',
    onlyPartOfStay: 'Seulement pour une partie du séjour',
    whyLabel: 'Pourquoi',
    whyHint: 'Enregistré sur le plan pour pouvoir expliquer la décision plus tard.',
    lockPlacement: 'Verrouiller le placement',
  },

  dateRangeCalendar: {
    previousYear: 'Année précédente',
    previousMonth: 'Mois précédent',
    nextMonth: 'Mois suivant',
    nextYear: 'Année suivante',
    pickArrival: "Choisissez le jour d'arrivée.",
    pickDeparture: (start) => `${start} → choisissez le jour de départ.`,
    summary: (start, end, nights) => `${start} → ${end} · ${nights}`,
  },

  dateRangeEditor: {
    addPeriod: 'Ajouter la période',
    addAPeriod: 'Ajouter une période',
  },

  dayByDayRecap: {
    titleFor: (sojournName) => `Jour par jour — ${sojournName}`,
    loadingRecap: 'Chargement du récapitulatif…',
    previousNight: '‹ Nuit précédente',
    nextNight: 'Nuit suivante ›',
    play: '▶ Lecture',
    pause: '⏸ Pause',
    nightOf: (index, total, date) => `Nuit ${index} sur ${total} · ${date}`,
    noHouses: 'Aucune maison ne fait partie de ce séjour pour le moment.',
    noPlan: 'Générez un plan pour voir qui dort où chaque nuit.',
    free: 'Libre',
    withoutABedTonight: 'Sans lit cette nuit',
  },

  calendarPage: {
    title: 'Calendrier',
    subtitle: 'Choisissez une date ou une période pour voir qui occupe chaque lit à ce moment-là.',
    showingSingle: (date) => `Affichage du ${date}. Cliquez un autre jour pour voir une période.`,
    showingRange: (start, end, nights) => `Affichage du ${start} au ${end} · ${nights}.`,
    loadingOccupancy: "Chargement de l'occupation…",
    noBedsTitle: 'Aucun lit pour le moment',
    noBedsDescription:
      'Ajoutez des maisons, des chambres et des lits pour voir leur occupation ici.',
    bedsOccupiedOf: (occupied, total) => `${occupied} occupé(s) sur ${total}`,
    withoutABedTitle: 'Sans lit',
    withoutABedDescription: "Le plan n'a pas pu placer tout le monde durant cette période.",
    free: 'Libre',
  },

  guestsPage: {
    title: 'Invités',
    subtitle: 'Les personnes à loger, avec leurs besoins et leurs affinités.',
    addGuest: 'Ajouter un invité',
    filterLabel: 'Filtrer les invités',
    filterPlaceholder: 'Filtrer par nom, trait ou note…',
    emptyTitle: 'Aucun invité pour le moment',
    emptyDescription: 'Ajoutez les personnes attendues, puis notez leurs préférences.',
    noMatchesTitle: 'Aucun invité ne correspond',
    noMatchesForQuery: (query) => `Personne ici ne correspond à « ${query} ».`,
    noMatchesForTags: "Personne n'a toutes les étiquettes sélectionnées.",
    clearFilters: 'Effacer les filtres',
    wantsToRoomWith: (names) => `Souhaite partager la chambre avec ${names}`,
    neverWith: (names) => `Jamais avec ${names}`,
    refuses: (names) => `Refuse ${names}`,
    someone: 'quelqu’un',
    aRoom: 'une chambre',
  },

  guestDialog: {
    addTitle: 'Ajouter un invité',
    editTitle: (name) => `Modifier ${name}`,
    nameLabel: 'Nom',
    genderLabel: 'Genre',
    genderHint: 'Aide le plan à garder les mêmes genres ensemble quand c’est possible.',
    regenerateAvatar: 'Redessiner l’avatar',
    traitsLabel: 'Traits',
    traitsHint: 'Ils changent où le plan est disposé à les placer.',
    tagsLabel: 'Étiquettes',
    tagsHint: 'Libellés libres pour regrouper et filtrer les invités.',
    addATag: 'Ajouter une étiquette',
    addTagPlaceholder: 'Ajoutez une étiquette et appuyez sur Entrée…',
    prefersToRoomWith: 'Préfère partager avec',
    prefersToRoomWithHint: 'Prio pour être mettre dans la même chambre.',
    mustNotRoomWith: 'Ne doit jamais partager avec',
    mustNotRoomWithHint: 'Une règle stricte : jamais la même chambre.',
    mustNotSleepIn: 'Ne doit jamais dormir dans',
    mustNotSleepInHint: 'Chambres où cet invité ne peut pas être placé.',
    notesLabel: 'Notes',
    notesHint: 'Facultatif, pour votre propre référence.',
    addGuest: "Ajouter l'invité",
    saveGuest: "Enregistrer l'invité",
    remove: 'Retirer',
    add: 'Ajouter…',
    nothingLeftToChoose: 'Plus rien à choisir.',
    nobodyYet: 'Personne pour le moment.',
    discardTitle: 'Abandonner les modifications ?',
    discardMessage:
      'Vous avez des modifications non enregistrées. Enregistrez-les ou abandonnez-les avant de quitter.',
    keepEditing: 'Continuer la modification',
    discardChanges: 'Abandonner les modifications',
  },

  housesPage: {
    title: 'Maisons',
    subtitle:
      'Les lieux où loger vos invités. Chaque maison a des chambres, et chaque chambre a des lits.',
    addHouse: 'Ajouter une maison',
    filterLabel: 'Filtrer les maisons',
    filterPlaceholder: 'Filtrer par nom ou adresse…',
    emptyTitle: 'Aucune maison pour le moment',
    emptyDescription: 'Ajoutez votre première maison pour commencer votre catalogue de logements.',
    noMatchesTitle: 'Aucune maison ne correspond',
    noMatchesForQuery: (query) => `Rien ici ne s'appelle « ${query} ».`,
    dialogTitle: 'Ajouter une maison',
    nameHint: 'Doit être unique parmi vos maisons.',
    addressLabel: 'Adresse',
    addressHint: 'Facultatif.',
    addHouseSubmit: 'Ajouter la maison',
  },

  houseDetail: {
    backToHouses: 'Retour aux maisons',
    addRoom: 'Ajouter une chambre',
    detailsTitle: 'Détails de la maison',
    nameLabel: 'Nom',
    addressLabel: 'Adresse',
    noRoomsTitle: 'Aucune chambre pour le moment',
    noRoomsDescription: 'Ajoutez une chambre, puis mettez-y des lits.',
  },

  roomCard: {
    holdsUpTo: (people) => `Accueille jusqu'à ${people}`,
    placesCapacity: (places, capacity) => `${places} places, capacité ${capacity}`,
    editRoom: 'Modifier la chambre',
    loadingBeds: 'Chargement des lits…',
    noBedsYet: 'Aucun lit dans cette chambre pour le moment.',
    addABed: 'Ajouter un lit',
    custom: ' (personnalisé)',
    sleeps: (n) => `couche ${n}`,
    wants: (names) => `souhaité par ${names}`,
  },

  roomDialog: {
    addTitle: 'Ajouter une chambre',
    editTitle: (name) => `Modifier ${name}`,
    houseLabel: 'Maison',
    houseHint: 'Une chambre appartient à une seule maison, et pour toujours.',
    chooseHouse: 'Choisir une maison…',
    nameLabel: 'Nom',
    nameHint: 'Unique au sein de sa maison.',
    maxPeopleLabel: 'Nombre maximal de personnes',
    maxPeopleHint:
      'Le plan ne met jamais plus de personnes que ce nombre dans la chambre, quel que soit le nombre de lits.',
    addRoom: 'Ajouter la chambre',
    saveRoom: 'Enregistrer la chambre',
  },

  bedDialog: {
    addTitle: 'Ajouter un lit',
    editTitle: (name) => `Modifier ${name}`,
    typeLabel: 'Type',
    typeOption: (label, sizeCm, sleeps) => `${label} (${sizeCm} cm, couche ${sleeps})`,
    optionLabel: 'Type de couchage',
    widthLabel: 'Largeur (cm)',
    widthHintDefault: (type, size) =>
      `Suit la valeur par défaut du type ${type} : ${size} cm. Saisissez un nombre pour la modifier.`,
    widthHintCustom: 'Défini manuellement. Videz le champ pour revenir au type de lit.',
    favoriteGuestsLabel: 'Invités favoris',
    favoriteGuestsHint:
      'Par ordre de préférence. Le plan essaie de satisfaire le premier en priorité.',
    moveUp: 'Monter',
    moveDown: 'Descendre',
    remove: 'Retirer',
    unknown: 'Inconnu',
    addAFavoriteGuest: 'Ajouter un invité favori',
    addGuestPlaceholder: 'Ajouter un invité…',
    addBed: 'Ajouter le lit',
    saveBed: 'Enregistrer le lit',
  },

  roomsPage: {
    title: 'Chambres',
    subtitle: 'Toutes vos chambres, quelle que soit la maison à laquelle elles appartiennent.',
    addRoom: 'Ajouter une chambre',
    filterLabel: 'Filtrer les chambres',
    filterPlaceholder: 'Filtrer par chambre ou maison…',
    noRoomsTitle: 'Aucune chambre pour le moment',
    noRoomsDescription: 'Ajoutez une chambre et choisissez à quelle maison elle appartient.',
    noHousesTitle: 'Aucune maison pour le moment',
    noHousesDescription: "Une chambre appartient à une maison : ajoutez d'abord une maison.",
    goToHouses: 'Aller aux maisons',
    noMatchesTitle: 'Aucune chambre ne correspond',
    noMatchesForQuery: (query) => `Rien ici ne s'appelle « ${query} ».`,
    holdsUpTo: (people) => `Accueille jusqu'à ${people}`,
    manageItsBeds: 'Gérer ses lits',
  },

  planPage: {
    backToSojourns: 'Retour aux séjours',
    titleFor: (sojournName) => `Plan — ${sojournName}`,
    noPlanYetFor: (start, end) => (
      <>
        Pas encore de plan pour {start} → {end}
      </>
    ),
    computedAt: (when) => `Calculé ${when}`,
    editTheSojourn: 'Modifier le séjour',
    generateThePlan: 'Générer le plan',
    regenerate: 'Régénérer',
    shareDoubleBedsLabel:
      'Laisser deux invités partager un lit double — uniquement des invités qui ont demandé à partager une chambre',
    noPlanTitle: 'Aucun plan pour le moment',
    noPlanDescription: 'Générez-en un pour voir qui dort où, et pourquoi.',
    nothingToArrangeTitle: 'Rien à organiser',
    nothingToArrangeDescription: "Aucun invité n'est attendu, quelle que soit la nuit.",
    periods: 'Périodes',
    periodsHint:
      "Le séjour est découpé partout où quelqu'un arrive, part, ou une maison ouvre ou ferme.",
    ofGuestNightsHoused: 'des nuitées logées',
    periodsLabel: 'périodes',
    bedOccupancy: "d'occupation des lits",
    midStayMoves: 'changements en cours de séjour',
    preferenceScore: 'score de préférence',
    toCompute: 'pour calculer',
    inThisRoom: (n) => `${n} dans cette chambre`,
    lockedByYou: 'Verrouillé par vous',
    hideDetails: 'Masquer les détails',
    why: 'Pourquoi ?',
    onSite: (n) => `${n} sur place`,
    nobodyCouldBeFoundABedFor: "Personne n'a pu être logé pour :",
  },

  planReasons: {
    equallyGoodAlternatives:
      'Alternatives tout aussi valables — appliquez-en une manuellement sans rien perdre',
  },

  admin: {
    title: 'Administration',
    subtitle:
      "Les comptes, les foyers qu'ils partagent, et un moyen de voir l'application à travers leurs yeux.",
    accountsTitle: 'Comptes',
    filterAccounts: 'Filtrer les comptes',
    filterAccountsPlaceholder: "Filtrer par nom d'utilisateur, foyer ou rôle…",
    noAccountMatches: 'Aucun compte ne correspond',
    noMatchesFor: (query) => `Rien ne correspond à « ${query} ».`,
    administrator: 'Administrateur',
    you: 'Vous',
    sharedByAccounts: (accounts) => `partagé par ${accounts}`,
    householdToThemselves: 'foyer personnel',
    viewAs: 'Voir comme',
    shareData: 'Partager les données',
    setPassword: 'Définir le mot de passe',
    leaveHousehold: 'Quitter le foyer',
    leaveTakingNothing: 'Quitter, sans rien emporter ?',
    householdsTitle: 'Foyers',
    householdsSubtitle:
      'Un foyer est le réservoir de maisons, invités et séjours que ses membres partagent.',
    filterHouseholds: 'Filtrer les foyers',
    filterHouseholdsPlaceholder: 'Filtrer par foyer ou membre…',
    noHouseholdMatches: 'Aucun foyer ne correspond',
    noMembers: 'Aucun membre',
    rename: 'Renommer',
    renameOf: (name) => `Renommer ${name}`,
    save: 'Enregistrer',
  },

  setPasswordDialog: {
    titleFor: (username) => `Définir un mot de passe pour ${username}`,
    warning: (username) => (
      <>
        Toute personne connectée en tant que {username} est immédiatement déconnectée.
        Communiquez-lui le nouveau mot de passe vous-même — il ne sera plus jamais affiché.
      </>
    ),
    newPasswordLabel: 'Nouveau mot de passe',
    passwordHint: 'Au moins 8 caractères.',
    setPassword: 'Définir le mot de passe',
  },

  shareHouseholdDialog: {
    sharedTitle: 'Partagé',
    sharedMessage: (username, householdName) => (
      <>
        {username} partage désormais le foyer « {householdName} ».
      </>
    ),
    renamedToAvoidClash: 'Renommé pour éviter un conflit',
    nothingRenamed: "Rien n'a eu besoin d'être renommé.",
    doneButton: 'Terminé',
    titleFor: (username) => `Partager les données de ${username}`,
    warning: (username) => (
      <>
        Le foyer de {username} est fusionné dans celui que vous choisissez, et chacun voit tout dans
        les deux. Cette action est irréversible — quitter un foyer partagé plus tard n&apos;en
        retire rien.
      </>
    ),
    shareWithLabel: 'Partager avec',
    shareWithHint: 'Leur foyer absorbe celui-ci.',
    chooseAnAccount: 'Choisir un compte…',
    share: 'Partager',
  },

  filterInput: {
    matchOf: (matchCount, totalCount) => `${matchCount} sur ${totalCount} affiché(s)`,
    clear: 'Effacer',
  },

  doorTransition: {
    greeting: 'Salut !',
  },
};

export type Dictionary = typeof en;

/**
 * "Single Standard", "Double Bunk Bottom" — what a bed is called everywhere it
 * is shown to a person. `bed.name` (`house-room-type-ordinal`) stays around as
 * a stable, derived identifier, but it is not for display.
 */
export function bedKindLabel(t: Dictionary, type: BedType, option: BedOption): string {
  return `${t.catalog.bedType[type]} ${t.catalog.bedKind[option]}`;
}
